/**
 * Analytics Dropdown Menu Components
 * Wrappers for DropdownMenu components with automatic event tracking
 */

import * as React from "react"
import {
  DropdownMenu as BaseDropdownMenu,
  DropdownMenuTrigger as BaseDropdownMenuTrigger,
  DropdownMenuItem as BaseDropdownMenuItem,
  DropdownMenuCheckboxItem as BaseDropdownMenuCheckboxItem,
  DropdownMenuRadioItem as BaseDropdownMenuRadioItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
} from "@/shared/widgets/dropdown-menu"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { useAnalytics } from "../hooks/useAnalytics"
import type { AnalyticsEventProviderType } from "../../application/services/AnalyticsService"

// Re-export non-interactive components as-is
export {
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}

// Wrapper for DropdownMenu root
export interface AnalyticsDropdownMenuProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Root> {
  /**
   * Event name to track when menu opens
   */
  openEventName?: string
  /**
   * Event name to track when menu closes
   */
  closeEventName?: string
  /**
   * Additional properties to include with events
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

export const DropdownMenu: React.FC<AnalyticsDropdownMenuProps> = ({
  openEventName,
  closeEventName,
  eventProperties,
  eventProviders,
  onOpenChange,
  ...props
}) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleOpenChange = (open: boolean) => {
    if (analytics && isInitialized) {
      try {
        const eventName = open ? openEventName : closeEventName
        if (eventName) {
          analytics.trackEvent(eventName, {
            ...eventProperties,
            menu_state: open ? 'open' : 'closed',
          }, { eventProviders })
        }
      } catch (error) {
        console.error('Error tracking dropdown menu event:', error)
      }
    }
    onOpenChange?.(open)
  }

  return (
    <BaseDropdownMenu
      onOpenChange={handleOpenChange}
      {...props}
    />
  )
}
DropdownMenu.displayName = "AnalyticsDropdownMenu"

// Wrapper for DropdownMenuTrigger
export interface AnalyticsDropdownMenuTriggerProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> {
  /**
   * Event name to track when trigger is clicked
   */
  eventName?: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

export const DropdownMenuTrigger = React.forwardRef<
  React.ComponentRef<typeof BaseDropdownMenuTrigger>,
  AnalyticsDropdownMenuTriggerProps
>(({ eventName, eventProperties, eventProviders, onClick, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (eventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(eventName, eventProperties, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error)
      }
    }
    onClick?.(e)
  }

  return (
    <BaseDropdownMenuTrigger
      ref={ref}
      onClick={handleClick}
      {...props}
    />
  )
})
DropdownMenuTrigger.displayName = "AnalyticsDropdownMenuTrigger"

// Wrapper for DropdownMenuItem
export interface AnalyticsDropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> {
  /**
   * Event name to track when item is selected
   */
  eventName?: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
  inset?: boolean
}

export const DropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof BaseDropdownMenuItem>,
  AnalyticsDropdownMenuItemProps
>(({ eventName, eventProperties, eventProviders, onSelect, children, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleSelect = (e: Event) => {
    if (eventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(eventName, {
          ...eventProperties,
          item_text: typeof children === 'string' ? children : 'menu_item',
        }, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error)
      }
    }
    onSelect?.(e)
  }

  return (
    <BaseDropdownMenuItem
      ref={ref}
      onSelect={handleSelect}
      {...props}
    >
      {children}
    </BaseDropdownMenuItem>
  )
})
DropdownMenuItem.displayName = "AnalyticsDropdownMenuItem"

// Wrapper for DropdownMenuCheckboxItem
export interface AnalyticsDropdownMenuCheckboxItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> {
  /**
   * Event name to track when checkbox is toggled
   */
  eventName?: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ComponentRef<typeof BaseDropdownMenuCheckboxItem>,
  AnalyticsDropdownMenuCheckboxItemProps
>(({ eventName, eventProperties, eventProviders, checked, onCheckedChange, children, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleCheckedChange = (checked: boolean) => {
    if (eventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(eventName, {
          ...eventProperties,
          checked,
          item_text: typeof children === 'string' ? children : 'checkbox_item',
        }, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error)
      }
    }
    onCheckedChange?.(checked)
  }

  return (
    <BaseDropdownMenuCheckboxItem
      ref={ref}
      checked={checked}
      onCheckedChange={handleCheckedChange}
      {...props}
    >
      {children}
    </BaseDropdownMenuCheckboxItem>
  )
})
DropdownMenuCheckboxItem.displayName = "AnalyticsDropdownMenuCheckboxItem"

// Wrapper for DropdownMenuRadioItem
export interface AnalyticsDropdownMenuRadioItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> {
  /**
   * Event name to track when radio item is selected
   */
  eventName?: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

export const DropdownMenuRadioItem = React.forwardRef<
  React.ComponentRef<typeof BaseDropdownMenuRadioItem>,
  AnalyticsDropdownMenuRadioItemProps
>(({ eventName, eventProperties, eventProviders, onSelect, children, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleSelect = (e: Event) => {
    if (eventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(eventName, {
          ...eventProperties,
          item_text: typeof children === 'string' ? children : 'radio_item',
        }, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error)
      }
    }
    onSelect?.(e)
  }

  return (
    <BaseDropdownMenuRadioItem
      ref={ref}
      onSelect={handleSelect}
      {...props}
    >
      {children}
    </BaseDropdownMenuRadioItem>
  )
})
DropdownMenuRadioItem.displayName = "AnalyticsDropdownMenuRadioItem"

